import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { HandoverPage } from '../../features/handover/ui/HandoverPage.js';
import { InboxPage } from '../../features/requisition/ui/InboxPage.js';
import { RequisitionCard } from '../../features/requisition/ui/RequisitionCard.js';
import { RequisitionCreatePage } from '../../features/requisition/ui/RequisitionCreatePage.js';
import { RequisitionsPage } from '../../features/requisition/ui/RequisitionsPage.js';
import { MyWarehousePage } from '../../features/subdivision/ui/MyWarehousePage.js';
import {
  parseRequisitionsSearch,
  type RequisitionsSearch,
} from '../../features/requisition/model/requisitions-search.js';
import { parseMyWarehouseSearch } from '../../features/subdivision/model/my-warehouse-search.js';

/**
 * Marshrut bilan sahifa orasidagi yupqa qatlam — `stock.tsx` dagi naqsh:
 * sahifalar routerni bilmaydi, ular `onCreated` kabi chaqiruv oladi.
 */

const LIST_PATH: string = '/requisitions';
const CREATE_PATH: string = '/requisitions/new';
const MY_WAREHOUSE_PATH: string = '/my-warehouse';

/** Kartochkaga o'tish bitta joyda — uch ekran ham shu yo'ldan yuradi. */
const cardTo = { to: '/requisitions/$requisitionId' } as const;

export const RequisitionCreateRoute = () => {
  const navigate = useNavigate();
  return (
    <RequisitionCreatePage
      /* Savat ombor bo'yicha bo'linadi, ya'ni bitta bosishdan bir nechta
         hujjat tug'ilishi mumkin. Bitta bo'lsa — o'sha kartochkaga, ko'p
         bo'lsa ro'yxatga: ixtiyoriy birini tanlab ochish "qolganlari
         qayerda?" degan savolni tug'dirardi. */
      onCreated={(ids) => {
        const only = ids.length === 1 ? ids[0] : undefined;
        void (only === undefined
          ? navigate({ to: LIST_PATH })
          : navigate({ ...cardTo, params: { requisitionId: only } }));
      }}
      onCancel={() => void navigate({ to: LIST_PATH })}
    />
  );
};

export const RequisitionCardRoute = () => {
  const navigate = useNavigate();
  const { requisitionId } = useParams({ from: '/shell/requisitions/$requisitionId' });
  return (
    <RequisitionCard
      requisitionId={requisitionId}
      onBack={() => void navigate({ to: LIST_PATH })}
    />
  );
};

export const RequisitionsListRoute = () => {
  const navigate = useNavigate();
  /* `strict: false` — bo'lim marshruti `NAV_ITEMS` dan umumiy quriladi va
     uning aniq turi bu yerda ma'lum emas. Qiymat baribir o'sha tekshiruvdan
     o'tadi (`parseRequisitionsSearch`). */
  const search = parseRequisitionsSearch(useSearch({ strict: false }));

  /* `replace` — bo'lim almashtirish tarixga yangi qadam QO'SHMAYDI: aks
     holda «Orqaga» tugmasi odamni bir sahifa ichidagi bo'limlar bo'ylab
     sudrab yurardi. */
  const go = (next: RequisitionsSearch): void =>
    void navigate({ to: LIST_PATH, search: parseRequisitionsSearch({ ...next }), replace: true });

  return (
    <RequisitionsPage
      onOpen={(id) => void navigate({ ...cardTo, params: { requisitionId: id } })}
      onCreate={() => void navigate({ to: CREATE_PATH })}
      view={search.view ?? 'requisitions'}
      onViewChange={(view) => go({ view })}
      {...(search.tab === undefined ? {} : { backorderTab: search.tab })}
      onBackorderTabChange={(tab) => go({ view: 'backorders', tab })}
    />
  );
};

/** Berish ekrani — kartochkaga o'tish shu bitta joydan boshqariladi. */
export const HandoverRoute = () => {
  const navigate = useNavigate();
  return (
    <HandoverPage
      onOpenRequisition={(id) => void navigate({ ...cardTo, params: { requisitionId: id } })}
    />
  );
};

/**
 * «Mening omborim» — talabnoma kartochkasiga o'tish shu yerdan boshqariladi.
 *
 * Sahifaning o'zi router ni bilmaydi: kartochka manzili bitta joyda —
 * marshrutlar faylida — turadi va xususiyatni routersiz sinash mumkin.
 */
export const MyWarehouseRoute = () => {
  const navigate = useNavigate();
  const search = parseMyWarehouseSearch(useSearch({ strict: false }));
  return (
    <MyWarehousePage
      onOpenRequisition={(id) => void navigate({ ...cardTo, params: { requisitionId: id } })}
      {...(search.section === undefined ? {} : { tab: search.section })}
      /* `replace` — bo'lim almashtirish tarixga qadam QO'SHMAYDI, aks holda
         «Orqaga» odamni bitta sahifa ichida sudrab yurardi. */
      onTabChange={(tab) =>
        void navigate({
          to: MY_WAREHOUSE_PATH,
          search: parseMyWarehouseSearch({ section: tab }),
          replace: true,
        })
      }
    />
  );
};

export const InboxRoute = () => {
  const navigate = useNavigate();
  return <InboxPage onOpen={(id) => void navigate({ ...cardTo, params: { requisitionId: id } })} />;
};
